<template>
  <v-expansion-panels class="panels" :value="0">
    <!-- Edit Dialog -->
    <v-dialog v-model="editItemMode" max-width="500px">
      <v-card>
        <v-card-title>ID: {{ itemId }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="searchContent"
            label="New Reference Number"
            clearable
          ></v-text-field>
          <v-btn
            tile
            color="success"
            class="buttonFooter"
            @click="updateItem(itemId)"
          >
            <v-icon left> mdi-pencil </v-icon>
            Edit
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" text @click="closeEditMode()"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog v-model="deleteItemMode" max-width="500px">
      <v-card>
        <v-card-title>ID: {{ itemId }}</v-card-title>
        <v-card-text> Are you sure? </v-card-text>
        <v-card-actions>
          <v-btn
            tile
            color="success"
            class="buttonFooter"
            @click="deleteItem(itemId)"
          >
            <v-icon left> mdi-delete </v-icon>
            Yes
          </v-btn>
          <v-btn
            tile
            color="error"
            class="buttonFooter"
            @click="closeEditMode()"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Items -->
    <v-expansion-panel class="panel" v-if="selectedDashboard.hasBlackList">
      <v-expansion-panel-header> Add Items </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-layout column>
          <v-flex>
            <v-row class="optionsBox">
              <v-col class="dropdown">
                <div v-if="addByRefNumber && !uploadFile">
                  <v-text-field
                    v-model="referenceNumber"
                    label="Reference Number"
                    clearable
                  ></v-text-field>
                </div>

                <div v-else-if="!addByRefNumber && !uploadFile">
                  <v-text-field
                    v-model="itemContent"
                    label="Item"
                    clearable
                    @change="clearData"
                  ></v-text-field>
                  <v-autocomplete
                    v-model="itemTypeManual"
                    :items="getAddItemType"
                    item-text="text"
                    item-value="value"
                    label="Select Type"
                  ></v-autocomplete>
                </div>

                <div v-else>
                  You can only upload files in XLSX format. You should use the
                  first line of your XLSX file as the header. You must add
                  "item" and "type" as the title. Otherwise, your file will not
                  be uploaded. Click to download a sample xlsx template.
                  <v-btn
                    class="ma-2"
                    outlined
                    href="https://jolt-assets.s3.eu-central-1.amazonaws.com/template/BlackList_Template.xlsx"
                    download
                  >
                    Download XLSX
                  </v-btn>
                </div>
              </v-col>

              <v-col class="dropdown">
                <v-checkbox
                  v-model="uploadFile"
                  label="File Upload"
                  :disabled="addByRefNumber"
                ></v-checkbox>
                <v-checkbox
                  v-model="addByRefNumber"
                  label="Add by Reference Number"
                  :disabled="uploadFile"
                ></v-checkbox>
              </v-col>
            </v-row>

            <v-row class="optionsBox">
              <v-col class="dropdown" v-if="uploadFile">
                <v-file-input
                  v-model="files"
                  color="green accent-4"
                  counter
                  label="File input"
                  multiple
                  placeholder="Select your xlsx files"
                  prepend-icon="mdi-paperclip"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  outlined
                  @change="readExcel"
                  :show-size="1000"
                >
                  <template v-slot:selection="{ index, text }">
                    <v-chip
                      v-if="index < 2"
                      color="green accent-4"
                      dark
                      label
                      small
                      >{{ text }}</v-chip
                    >
                    <span
                      v-else-if="index === 2"
                      class="overline grey--text text--darken-3 mx-2"
                      >+{{ files.length - 2 }} File(s)</span
                    >
                  </template>
                </v-file-input>
              </v-col>

              <v-col class="searchBox">
                <v-row>
                  <v-btn
                    v-if="!uploadFile"
                    class="button"
                    color="green darken-2"
                    @click="addItem()"
                    :disabled="
                      (addByRefNumber && !referenceNumber) ||
                      (!addByRefNumber && (!itemContent || !itemTypeManual))
                    "
                  >
                    Add to BlackList
                  </v-btn>
                  <v-btn
                    v-else
                    class="button"
                    color="green darken-2"
                    :disabled="!fileStatus"
                    @click="uploadFileItem()"
                  >
                    <v-icon dark> mdi-cloud-upload </v-icon>
                    Upload File
                  </v-btn>
                </v-row>
              </v-col>
            </v-row>

            <v-row class="optionsBox">
              <v-col>
                <v-alert v-if="message !== ''" :type="this.messageType">
                  <v-icon v-if="this.messageType === 'success'" dark>
                    mdi-checkbox-marked-circle
                  </v-icon>
                  {{ message }}
                </v-alert>
              </v-col>
            </v-row>
          </v-flex>
        </v-layout>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel class="panel" v-if="selectedDashboard.hasBlackList">
      <v-expansion-panel-header>Search Items</v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-layout column>
          <v-flex>
            <v-row class="optionsBox">
              <!-- Dropdown: searchBy -->
              <v-col class="dropdown">
                <v-autocomplete
                  v-model="searchBy"
                  :items="searchByOptions"
                  item-text="text"
                  item-value="value"
                  label="Search By"
                  clearable
                ></v-autocomplete>
              </v-col>

              <v-col class="dropdown">
                <v-text-field
                  v-model="searchContent"
                  :label="
                    searchBy === 'referenceNumber' ? 'Reference Number' : 'Item'
                  "
                  clearable
                ></v-text-field>
              </v-col>

              <v-col class="searchBox">
                <v-btn
                  class="button"
                  color="green darken-2"
                  :disabled="!searchContent || !searchBy"
                  @click="searchItem()"
                >
                  Search
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              v-if="searchClick"
              :items="searchData"
              :no-data-text="'Not found'"
              :disable-pagination="true"
              :hide-default-footer="true"
              :headers="headers"
            >
              <template slot="item" slot-scope="{ item }">
                <tr>
                  <td>{{ item.id }}</td>
                  <td>{{ item.item }}</td>
                  <td>{{ item.type }}</td>
                  <td>{{ formatDate(item.createdAt) }}</td>
                  <td>{{ formatDate(item.updatedAt) }}</td>
                  <td>
                    <v-btn
                      class="mx-2"
                      fab
                      dark
                      small
                      color="error"
                      @click="deleteItemDialog(item.id)"
                    >
                      <v-icon dark> mdi-delete </v-icon>
                    </v-btn>
                    <v-btn
                      class="mx-2"
                      fab
                      dark
                      small
                      color="warning"
                      @click="editItem(item)"
                    >
                      <v-icon dark> mdi-pencil </v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template slot="no-data">
                <v-alert
                  :value="true"
                  icon="mdi-cancel"
                  class="display-1 font-weight-thin"
                  >No items found for now ¯\_(ツ)_/¯</v-alert
                >
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel class="panel" v-if="selectedDashboard.hasBlackList">
      <v-expansion-panel-header>All Items</v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-layout column>
          <v-flex style>
            <v-row class="optionsBox">
              <v-col class="dropdown">
                <v-autocomplete
                  v-model="itemType"
                  :items="getItemType"
                  item-text="text"
                  item-value="value"
                  label="Select Item Type"
                  @change="clearData"
                >
                </v-autocomplete>
              </v-col>
              <v-col>
                <v-btn
                  class="panel"
                  color="green darken-2"
                  :disabled="isPending"
                  @click="getAllItem(true)"
                  >Search
                </v-btn>
              </v-col>
            </v-row>
            <v-data-table
              v-if="isClick"
              :items="allData"
              :no-data-text="'Not found'"
              :disable-pagination="true"
              :hide-default-footer="true"
              :headers="headers"
            >
              <template slot="item" slot-scope="{ item }">
                <tr>
                  <td>{{ item.id }}</td>
                  <td>{{ item.item }}</td>
                  <td>{{ item.type }}</td>
                  <td>{{ formatDate(item.createdAt) }}</td>
                  <td>{{ formatDate(item.updatedAt) }}</td>
                  <td>
                    <v-btn
                      class="mx-2"
                      fab
                      dark
                      small
                      color="error"
                      @click="deleteItemDialog(item.id)"
                    >
                      <v-icon dark> mdi-delete </v-icon>
                    </v-btn>
                    <v-btn
                      class="mx-2"
                      fab
                      dark
                      small
                      color="warning"
                      @click="editItem(item)"
                    >
                      <v-icon dark> mdi-pencil </v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template slot="no-data">
                <v-alert
                  :value="true"
                  icon="mdi-cancel"
                  class="display-1 font-weight-thin"
                  >No items found for now ¯\_(ツ)_/¯</v-alert
                >
              </template>
              <template slot="footer">
                <v-btn
                  v-if="showLoadMore"
                  class="buttonFooter"
                  color="teal darken-1"
                  :disabled="isPending"
                  @click="getAllItem(false)"
                  >Load More
                </v-btn>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel class="panel" v-else>
      <v-expansion-panel-header>BlackList</v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-layout column>
          <v-flex style>
            <v-alert
              :value="true"
              icon="mdi-cancel"
              class="display-1 font-weight-thin"
              >BlackList has not yet been added for this campaign.
              ¯\_(ツ)_/¯</v-alert
            >
          </v-flex>
        </v-layout>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import axios from "axios";
import moment from "moment";
import { utils, read } from "xlsx";

export default {
  data() {
    return {
      editItemMode: false,
      uploadFile: false,
      addByRefNumber: false,
      fileStatus: false,
      searchClick: false,
      showLoadMore: true,
      deleteItemMode: false,
      isClick: false,
      itemContent: "",
      referenceNumber: "",
      itemTypeManual: "",
      itemId: "",
      isPending: false,
      count: 1,
      searchContent: "",
      excelData: [],
      searchData: [],
      searchBy: "referenceNumber",
      searchByOptions: [
        { text: "Reference Number", value: "referenceNumber" },
        { text: "Manually", value: "item" },
      ],
      allData: [],
      itemType: "all",
      files: [],
      messageType: "",
      message: "",
      headers: [
        { sortable: false, align: "left", text: "Id" },
        { sortable: false, text: "Item" },
        { sortable: false, text: "Type" },
        { sortable: false, text: "Created At" },
        { sortable: false, text: "Updated At" },
        { sortable: false, text: "Delete / Edit" },
      ],
    };
  },
  computed: {
    ...mapGetters(["userToken", "selectedDashboard"]),
    getItemType() {
      return [
        { text: "All", value: "all" },
        { text: "Online Sms", value: "onlineSms" },
        { text: "Email", value: "email" },
        { text: "Iban", value: "iban" },
        { text: "Mobile", value: "mobile" },
      ];
    },
    getAddItemType() {
      return [
        { text: "Online Sms", value: "onlineSms" },
        { text: "Email", value: "email" },
        { text: "Iban", value: "iban" },
        { text: "Mobile", value: "mobile" },
      ];
    },
  },
  methods: {
    ...mapActions(["getDashboardData"]),
    readExcel() {
      this.excelData = [];
      this.message = "";
      this.fileStatus = false;
      var input = this.files;
      var reader = new FileReader();
      reader.onload = () => {
        var fileData = reader.result;
        var wb = read(fileData, { type: "binary" });
        wb.SheetNames.forEach((sheetName) => {
          var rowObj = utils.sheet_to_json(wb.Sheets[sheetName]);
          this.excelData = rowObj; //JSON.stringify(rowObj);
        });
      };
      reader.readAsBinaryString(input[0]);
      this.setData();
    },
    setData() {
      setTimeout(() => {
        this.fileStatus = true;
        let tempArry = this.excelData;
        this.excelData = [];
        var index = 0;
        const lastIndex = tempArry.length;

        for (index = 0; index < lastIndex; index++) {
          const item = tempArry[index]["item"];
          const type = tempArry[index]["type"];
          this.excelData.push({ item, type });
        }
        console.log(this.excelData);
      }, 300);
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    addItem() {
      const data = { sessid: this.userToken };

      if (this.addByRefNumber) {
        data.referenceNumber = this.referenceNumber;
      } else {
        data.item = this.itemContent;
        data.type = this.itemTypeManual;
      }

      axios
        .post(
          `${this.selectedDashboard.endPoints.base}${this.selectedDashboard.endPoints.addBlackListItem}`,
          data
        )
        .then((response) => {
          this.messageType = response.data.response ? "success" : "error";
          this.message = response.data.message;
        })
        .catch(() => {});
    },
    uploadFileItem() {
      const data = {
        sessid: this.userToken,
        file: JSON.stringify(this.excelData),
      };
      axios({
        method: "post",
        url: `${this.selectedDashboard.endPoints.base}${this.selectedDashboard.endPoints.uploadBlackListFile}`,
        data,
      })
        .then((response) => {
          if (response.data.response) {
            if (response.data.warning) {
              this.messageType = "warning";
              this.message = response.data.message;
            } else {
              this.messageType = "success";
              this.message = response.data.message;
            }
          } else {
            this.messageType = "error";
            this.message = response.data.message;
          }
        })
        .catch((er) => {});
    },
    clearData() {
      this.messageType = "";
      this.message = "";
      this.allData = [];
    },

    searchItem() {
      const data = { sessid: this.userToken };

      if (this.searchBy === "referenceNumber") {
        data.referenceNumber = this.searchContent;
      } else if (this.searchBy === "item") {
        data.item = this.searchContent;
      }

      axios
        .post(
          `${this.selectedDashboard.endPoints.base}${this.selectedDashboard.endPoints.searchBlackListItem}`,
          data
        )
        .then((response) => {
          if (response.data.response) {
            this.searchData = response.data.items;
            this.searchClick = true;
          } else {
            this.searchData = [];
            this.searchClick = false;
          }
        })
        .catch(() => {});
    },

    editItem(item) {
      this.editItemMode = true;
      this.searchContent = item.item;
      this.itemId = item.id;
    },

    closeEditMode() {
      this.editItemMode = false;
      this.deleteItemMode = false;
      this.searchContent = "";
      this.itemId = "";
    },

    updateItem(id) {
      const selectedItem =
        this.searchData.find((i) => i.id === id) ||
        this.allData.find((i) => i.id === id);

      if (!selectedItem) return;

      const data = {
        sessid: this.userToken,
        item: this.searchContent,
        type: selectedItem.type,
        id,
      };

      axios
        .post(
          `${this.selectedDashboard.endPoints.base}${this.selectedDashboard.endPoints.updateBlackListItem}`,
          data
        )
        .then((response) => {
          if (response.data.response) {
            this.editItemMode = false;

            this.searchData = this.searchData.map((i) =>
              i.id === id ? { ...i, item: this.searchContent } : i
            );
            this.allData = this.allData.map((i) =>
              i.id === id ? { ...i, item: this.searchContent } : i
            );
          }
        })
        .catch(() => {});
    },

    deleteItemDialog(id) {
      this.deleteItemMode = true;
      this.itemId = id;
    },

    deleteItem(id) {
      const data = { sessid: this.userToken, id };
      axios
        .post(
          `${this.selectedDashboard.endPoints.base}${this.selectedDashboard.endPoints.deleteBlackListItem}`,
          data
        )
        .then((response) => {
          this.deleteItemMode = false;
          if (response.data.response) {
            this.searchData = this.searchData.filter((i) => i.id !== id);

            this.allData = this.allData.filter((i) => i.id !== id);
          }
        })
        .catch(() => {
          this.deleteItemMode = false;
        });
    },

    getAllItem(firstClick) {
      this.isClick = true;
      this.isPending = true;
      this.count = firstClick ? 1 : this.count + 1;

      const data = {
        sessid: this.userToken,
        count: this.count,
      };

      if (this.itemType && this.itemType !== "all") {
        data.type = this.itemType;
      }

      axios
        .post(
          `${this.selectedDashboard.endPoints.base}${this.selectedDashboard.endPoints.getAllBlackListItem}`,
          data
        )
        .then((response) => {
          if (response.data.response) {
            if (firstClick) {
              this.allData = response.data.items;
            } else {
              this.allData = [...this.allData, ...response.data.items];
            }
            this.isPending = false;
            this.showLoadMore = !response.data.done;
          }
        })
        .catch(() => {
          this.isPending = false;
        });
    },
  },
  mounted() {
    if ("name" in this.selectedDashboard === false) {
      const dashboard = JSON.parse(localStorage.selectedDashboard);
      this.$store.commit("setDashboard", dashboard);
    }
    this.$store.commit("setUserRoles", JSON.parse(localStorage.userRoles));
  },
};
</script>

<style scoped>
.optionsBox {
  margin-left: 12px;
  margin-right: 12px;
  margin-top: 12px;
}
.dropdown {
  max-width: 50%;
}
.searchBox {
  max-width: 30%;
  display: flex;
  align-items: center;
}
.panel {
  margin-top: 12px;
}
.panels {
  margin-left: 20px;
  margin-right: 20px;
}
.buttonFooter {
  margin-top: 20px;
  margin-bottom: 20px;
  display: block;
  margin-left: auto;
  margin-right: auto;
}
</style>
